import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LayoutGrid, Calendar, GanttChart, List, Loader2 } from 'lucide-react';
import { useAppStore } from '../store';
import { useAuth } from '../context/AuthContext';
import { TaskBoardTask, TaskProject, ProjectStickyNote } from '../types';
import ProjectSelector from '../components/projects/ProjectSelector';
import ProjectFiltersBar from '../components/projects/ProjectFiltersBar';
import ProjectBoardView from '../components/projects/ProjectBoardView';
import ProjectCalendarView from '../components/projects/ProjectCalendarView';
import ProjectTimelineView from '../components/projects/ProjectTimelineView';
import ProjectListView from '../components/projects/ProjectListView';
import ProjectTaskPanel from '../components/projects/ProjectTaskPanel';
import ProjectModal from '../components/projects/ProjectModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import {
  ProjectView, CalendarMode, TimelineMode, VIEW_LABELS, isTaskDone, ColumnAggregationMode,
} from '../utils/projectConstants';
import {
  defaultFilters, filterTasks, collectTags,
  getProjectColumns, appendActivity, getColumnColors, getProjectMutedBg, getColumnAggregations,
} from '../utils/projectUtils';
import { ProjectStatus } from '../utils/projectConstants';
import {
  SystemUser,
  buildAssigneeFilterOptions,
  fetchSystemUsers,
  getSystemUserByAssignee,
  normalizeAssigneeInput,
} from '../utils/projectAssignees';
import { NotificationService } from '../utils/notificationService';

const VIEW_ICONS: Record<ProjectView, React.ReactNode> = {
  board: <LayoutGrid className="w-3.5 h-3.5" />,
  calendar: <Calendar className="w-3.5 h-3.5" />,
  timeline: <GanttChart className="w-3.5 h-3.5" />,
  list: <List className="w-3.5 h-3.5" />,
};

const TodoList: React.FC = () => {
  const { user } = useAuth();
  const {
    darkMode,
    taskProjects,
    taskBoardTasks,
    fetchTaskProjects,
    fetchProjectTasks,
    addTaskProject,
    updateTaskProject,
    reorderTaskProjects,
    deleteTaskProject,
    addTaskBoardTask,
    updateTaskBoardTask,
    deleteTaskBoardTask,
  } = useAppStore();

  const [loading, setLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [view, setView] = useState<ProjectView>('board');
  const [calendarMode, setCalendarMode] = useState<CalendarMode>('month');
  const [timelineMode, setTimelineMode] = useState<TimelineMode>('month');
  const [filters, setFilters] = useState(defaultFilters());
  const [selectedTask, setSelectedTask] = useState<TaskBoardTask | null>(null);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<TaskProject | null>(null);
  const [deleteProjectConfirm, setDeleteProjectConfirm] = useState<TaskProject | null>(null);
  const [pendingCreateDate, setPendingCreateDate] = useState<string | null>(null);
  const [systemUsers, setSystemUsers] = useState<SystemUser[]>([]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const [users] = await Promise.all([fetchSystemUsers(), fetchTaskProjects()]);
      setSystemUsers(users);
      setLoading(false);
    };
    init();
  }, [fetchTaskProjects]);

  useEffect(() => {
    const active = taskProjects.filter(p => p.status !== 'archived');
    if (!selectedProjectId && active.length > 0) {
      setSelectedProjectId(active[0].id);
    }
  }, [taskProjects, selectedProjectId]);

  useEffect(() => {
    if (selectedProjectId) fetchProjectTasks(selectedProjectId);
  }, [selectedProjectId, fetchProjectTasks]);

  const selectedProject = useMemo(
    () => taskProjects.find(p => p.id === selectedProjectId) || null,
    [taskProjects, selectedProjectId]
  );

  const projectTasks = useMemo(
    () => taskBoardTasks.filter(t => t.project_id === selectedProjectId),
    [taskBoardTasks, selectedProjectId]
  );

  const filteredTasks = useMemo(() => filterTasks(projectTasks, {
    ...filters,
    currentUserEmail: user?.email,
    currentUserName: user?.user_metadata?.full_name || user?.email?.split('@')[0],
    systemUsers,
  }), [projectTasks, filters, user, systemUsers]);

  const assigneeOptions = useMemo(
    () => buildAssigneeFilterOptions(projectTasks, systemUsers),
    [projectTasks, systemUsers]
  );

  const tags = useMemo(() => collectTags(projectTasks), [projectTasks]);

  const handleCreateTask = useCallback(async (title: string, status: string, dueDate?: string) => {
    if (!selectedProjectId) return;
    const maxPos = projectTasks.reduce((m, t) => Math.max(m, t.position ?? 0), 0);
    const task = await addTaskBoardTask({
      board_id: null,
      project_id: selectedProjectId,
      title,
      description: null,
      status,
      due_date: dueDate || pendingCreateDate || null,
      start_date: null,
      priority: 'medium',
      assignee: null,
      position: maxPos + 1000,
      completed: isTaskDone(status),
      is_archived: false,
      tags: [],
      checklist: [],
      comments: [],
      attachments: [],
      activity: [{ type: 'create', message: 'Tarea creada', author: user?.email, created_at: new Date().toISOString() }],
    });
    setPendingCreateDate(null);
    if (task) setSelectedTask(task);
  }, [selectedProjectId, projectTasks, addTaskBoardTask, pendingCreateDate, user?.email]);

  const handleMoveTask = useCallback(async (taskId: string, status: string, position: number) => {
    await updateTaskBoardTask(taskId, {
      status,
      position,
      completed: isTaskDone(status),
    });
  }, [updateTaskBoardTask]);

  const handleReorderColumns = useCallback(async (columns: string[]) => {
    if (!selectedProject) return;
    await updateTaskProject(selectedProject.id, { status_columns: columns });
  }, [selectedProject, updateTaskProject]);

  const handleRenameColumn = useCallback(async (oldName: string, newName: string) => {
    if (!selectedProject) return;
    const cols = getProjectColumns(selectedProject).map(c => c === oldName ? newName : c);
    const colors = { ...getColumnColors(selectedProject) };
    const aggregations = { ...getColumnAggregations(selectedProject) };
    if (colors[oldName]) {
      colors[newName] = colors[oldName];
      delete colors[oldName];
    }
    if (aggregations[oldName]) {
      aggregations[newName] = aggregations[oldName];
      delete aggregations[oldName];
    }
    await updateTaskProject(selectedProject.id, { status_columns: cols, column_colors: colors, column_aggregations: aggregations });
    for (const task of projectTasks.filter(t => t.status === oldName)) {
      await updateTaskBoardTask(task.id, { status: newName });
    }
  }, [selectedProject, projectTasks, updateTaskProject, updateTaskBoardTask]);

  const handleDeleteColumn = useCallback(async (columnName: string) => {
    if (!selectedProject) return;
    const cols = getProjectColumns(selectedProject).filter(c => c !== columnName);
    const fallback = cols[0] || 'To-do';
    const colors = { ...getColumnColors(selectedProject) };
    const aggregations = { ...getColumnAggregations(selectedProject) };
    delete colors[columnName];
    delete aggregations[columnName];
    await updateTaskProject(selectedProject.id, { status_columns: cols, column_colors: colors, column_aggregations: aggregations });
    for (const task of projectTasks.filter(t => t.status === columnName)) {
      await updateTaskBoardTask(task.id, { status: fallback });
    }
  }, [selectedProject, projectTasks, updateTaskProject, updateTaskBoardTask]);

  const handleUpdateColumnColor = useCallback(async (columnName: string, color: string | null) => {
    if (!selectedProject) return;
    const colors = { ...getColumnColors(selectedProject) };
    if (color) colors[columnName] = color;
    else delete colors[columnName];
    await updateTaskProject(selectedProject.id, { column_colors: colors });
  }, [selectedProject, updateTaskProject]);

  const handleUpdateColumnAggregation = useCallback(async (columnName: string, mode: ColumnAggregationMode) => {
    if (!selectedProject) return;
    const aggregations = { ...getColumnAggregations(selectedProject) };
    if (mode === 'none') delete aggregations[columnName];
    else aggregations[columnName] = mode;
    await updateTaskProject(selectedProject.id, { column_aggregations: aggregations });
  }, [selectedProject, updateTaskProject]);

  const handleAddColumn = useCallback(async (name: string) => {
    if (!selectedProject) return;
    const cols = [...getProjectColumns(selectedProject), name];
    await updateTaskProject(selectedProject.id, { status_columns: cols });
  }, [selectedProject, updateTaskProject]);

  const handleUpdateStickyNotes = useCallback(async (notes: ProjectStickyNote[]) => {
    if (!selectedProject) return;
    await updateTaskProject(selectedProject.id, { sticky_notes: notes });
  }, [selectedProject, updateTaskProject]);

  const handleSaveProject = async (data: {
    name: string; description: string; color: string;
    status: ProjectStatus; status_columns: string[];
  }) => {
    if (editingProject) {
      await updateTaskProject(editingProject.id, data);
    } else {
      const created = await addTaskProject({
        name: data.name,
        description: data.description || null,
        color: data.color,
        status: data.status,
        status_columns: data.status_columns,
      });
      if (created) setSelectedProjectId(created.id);
    }
  };

  const handleArchiveTask = async (id: string) => {
    const task = taskBoardTasks.find(t => t.id === id);
    if (!task) return;
    await updateTaskBoardTask(id, {
      is_archived: true,
      activity: appendActivity(task, 'Archivó la tarea', user?.email),
    });
    setSelectedTask(null);
  };

  const handleDeleteTask = async (id: string) => {
    await deleteTaskBoardTask(id);
    setSelectedTask(null);
  };

  const notifyAssigneeIfNeeded = useCallback(async (
    taskId: string,
    prevAssignee: string | null | undefined,
    nextAssignee: string | null | undefined,
    taskTitle: string
  ) => {
    const normalized = nextAssignee
      ? normalizeAssigneeInput(nextAssignee, systemUsers)
      : null;
    const prevNormalized = prevAssignee
      ? normalizeAssigneeInput(prevAssignee, systemUsers)
      : null;
    if (!normalized || normalized === prevNormalized) return;

    const target = getSystemUserByAssignee(normalized, systemUsers);
    if (!target || target.id === user?.id) return;

    await NotificationService.notifyProjectTaskAssigned(target.id, {
      taskId,
      taskTitle,
      projectName: selectedProject?.name,
      assignedBy: user?.email ?? null,
    });
  }, [systemUsers, user?.id, user?.email, selectedProject?.name]);

  const handleTaskUpdate = useCallback(async (
    id: string,
    updates: Partial<TaskBoardTask>
  ) => {
    const prev = taskBoardTasks.find(t => t.id === id);
    const updated = await updateTaskBoardTask(id, updates);
    if (updated && 'assignee' in updates) {
      await notifyAssigneeIfNeeded(
        id,
        prev?.assignee,
        updates.assignee,
        updated.title
      );
    }
    return updated;
  }, [taskBoardTasks, updateTaskBoardTask, notifyAssigneeIfNeeded]);

  const handleCreateOnDate = (date: string) => {
    setPendingCreateDate(date);
    handleCreateTask('Nueva tarea', getProjectColumns(selectedProject)[0], date);
  };

  const projectBgAlpha = darkMode ? 0.12 : 0.18;
  const projectPageBg = getProjectMutedBg(selectedProject, projectBgAlpha);
  const selectorFadeBg = projectPageBg ?? (darkMode ? 'rgb(3 7 18 / 0.95)' : 'rgb(249 250 251 / 0.95)');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col flex-1 min-h-0 w-full overflow-hidden p-4 sm:p-6 lg:p-8 transition-colors duration-300 ${
        projectPageBg ? '' : 'bg-gray-50/50 dark:bg-gray-950'
      }`}
      style={projectPageBg ? { backgroundColor: projectPageBg } : undefined}
    >
      {/* Header */}
      <div className="flex flex-col gap-3 mb-4 flex-shrink-0">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <h1 className="text-lg font-bold text-gray-900 dark:text-white flex-shrink-0">Proyectos</h1>
            <ProjectSelector
              projects={taskProjects}
              selectedId={selectedProjectId}
              onSelect={setSelectedProjectId}
              onCreate={() => { setEditingProject(null); setProjectModalOpen(true); }}
              onRename={async (p, name) => { await updateTaskProject(p.id, { name }); }}
              onDelete={p => setDeleteProjectConfirm(p)}
              onEdit={p => { setEditingProject(p); setProjectModalOpen(true); }}
              onReorder={reorderTaskProjects}
              fadeBackground={selectorFadeBg}
            />
          </div>

          <div className="flex rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900">
            {(Object.keys(VIEW_LABELS) as ProjectView[]).map(v => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 transition-colors ${
                  view === v
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                {VIEW_ICONS[v]}
                {VIEW_LABELS[v]}
              </button>
            ))}
          </div>
        </div>

        <ProjectFiltersBar
          filters={filters}
          onChange={patch => setFilters(prev => ({ ...prev, ...patch }))}
          assigneeOptions={assigneeOptions}
          tags={tags}
        />
      </div>

      {/* Content */}
      {!selectedProject ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-20">
          <LayoutGrid className="w-10 h-10 text-gray-300 mb-3" />
          <p className="text-sm text-gray-500 mb-4">Seleccioná o creá un proyecto para comenzar</p>
          <button
            type="button"
            onClick={() => { setEditingProject(null); setProjectModalOpen(true); }}
            className="text-sm px-4 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700"
          >
            Crear proyecto
          </button>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col">
          {view === 'board' && (
            <ProjectBoardView
              project={selectedProject}
              tasks={filteredTasks}
              onTaskClick={setSelectedTask}
              onCreateTask={handleCreateTask}
              onMoveTask={handleMoveTask}
              onReorderColumns={handleReorderColumns}
              onRenameColumn={handleRenameColumn}
              onDeleteColumn={handleDeleteColumn}
              onAddColumn={handleAddColumn}
              onUpdateColumnColor={handleUpdateColumnColor}
              onUpdateColumnAggregation={handleUpdateColumnAggregation}
              onUpdateStickyNotes={handleUpdateStickyNotes}
            />
          )}
          {view === 'calendar' && (
            <ProjectCalendarView
              tasks={filteredTasks}
              mode={calendarMode}
              onModeChange={setCalendarMode}
              onTaskClick={setSelectedTask}
              onCreateOnDate={handleCreateOnDate}
            />
          )}
          {view === 'timeline' && (
            <ProjectTimelineView
              tasks={filteredTasks}
              mode={timelineMode}
              onModeChange={setTimelineMode}
              onTaskClick={setSelectedTask}
            />
          )}
          {view === 'list' && (
            <ProjectListView tasks={filteredTasks} onTaskClick={setSelectedTask} />
          )}
        </div>
      )}

      <ProjectTaskPanel
        task={selectedTask}
        project={selectedProject}
        systemUsers={systemUsers}
        currentUserEmail={user?.email}
        onClose={() => setSelectedTask(null)}
        onUpdate={async (id, updates) => {
          const updated = await handleTaskUpdate(id, updates);
          setSelectedTask(prev => (prev?.id === id ? updated : prev));
          return updated;
        }}
        onArchive={handleArchiveTask}
        onDelete={handleDeleteTask}
      />

      <ProjectModal
        isOpen={projectModalOpen}
        project={editingProject}
        onClose={() => { setProjectModalOpen(false); setEditingProject(null); }}
        onSave={handleSaveProject}
      />

      <ConfirmModal
        isOpen={!!deleteProjectConfirm}
        onClose={() => setDeleteProjectConfirm(null)}
        onConfirm={async () => {
          if (deleteProjectConfirm) {
            await deleteTaskProject(deleteProjectConfirm.id);
            if (selectedProjectId === deleteProjectConfirm.id) {
              const remaining = taskProjects.filter(
                p => p.id !== deleteProjectConfirm.id && p.status !== 'archived'
              );
              setSelectedProjectId(remaining[0]?.id ?? null);
            }
            setDeleteProjectConfirm(null);
          }
        }}
        title="Eliminar proyecto"
        message={`¿Eliminar "${deleteProjectConfirm?.name}" y todas sus tareas? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
        overlayStyle="dim"
      />
    </div>
  );
};

export default TodoList;
